import React, { useCallback, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ArrowLeft,
  Upload,
  File as LucideFile,
  X,
  FileText,
  Image,
  FileCode,
  Trash2,
  AlertCircle
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import ProjectFilesService from '../../services/ProjectFilesService';

// ----------------------------------------------------------------
// Styled Components
// ----------------------------------------------------------------
const StyledButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.9)' : '#0385FF',
    color: isWallpaperMode ? '#000000' : '#FFFFFF',
    borderRadius: '20px',
    textTransform: 'none',
    padding: '6px 16px',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.8)' : '#0371db',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      marginRight: 6,
    },
  };
});

// ----------------------------------------------------------------
// AlertContainer
// ----------------------------------------------------------------
const AlertContainer = ({ message, iconColor, onClose }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        // Ajustamos para que no fuerce el 100% de la pantalla
        // y se adapte al ancho del contenedor padre.
        maxWidth: '100%',
        backgroundColor: isWallpaperMode
          ? 'rgba(255, 255, 255, 0.12)'
          : (isDarkMode ? 'rgba(254, 243, 199, 0.1)' : 'rgb(254, 243, 199)'),
        border: isWallpaperMode
          ? '1px solid rgba(255, 255, 255, 0.2)'
          : `1px solid ${isDarkMode ? 'rgb(180, 150, 50)' : 'rgb(252, 211, 77)'}`,
        borderRadius: '28px',
        padding: '12px 16px',
        marginBottom: '8px',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <AlertCircle size={20} style={{ color: iconColor }} />
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: isWallpaperMode
              ? '#FFFFFF'
              : (isDarkMode ? 'rgb(252, 211, 77)' : 'rgb(120, 53, 15)'),
            fontWeight: 500,
          }}
        >
          {message}
        </Typography>
      </Box>
      {onClose && (
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            padding: '4px',
            color: isWallpaperMode
              ? '#FFFFFF'
              : (isDarkMode ? 'rgb(252, 211, 77)' : 'rgb(120, 53, 15)'),
            '&:hover': {
              backgroundColor: isWallpaperMode
                ? 'rgba(255, 255, 255, 0.12)'
                : (isDarkMode
                  ? 'rgba(252, 211, 77, 0.1)'
                  : 'rgba(120, 53, 15, 0.1)'),
            },
          }}
        >
          <X size={16} />
        </IconButton>
      )}
    </Box>
  );
};

// ----------------------------------------------------------------
// FileIcon helper
// ----------------------------------------------------------------
const FileIcon = ({ fileType, size = 20 }) => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const color = isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#fff' : '#000');

  switch (fileType) {
    case 'image':
      return <Image size={size} color={color} />;
    case 'code':
      return <FileCode size={size} color={color} />;
    case 'document':
      return <FileText size={size} color={color} />;
    default:
      return <LucideFile size={size} color={color} />;
  }
};

const getFileType = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(extension)) return 'image';
  if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c'].includes(extension)) return 'code';
  if (['doc', 'docx', 'pdf', 'txt', 'csv', 'xls', 'xlsx'].includes(extension)) return 'document';
  return 'file';
};

// ----------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------
const FilesView = ({ project, onBack, user, isModal = false, open = false, onClose }) => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const { t } = useTranslation(); // Inicializar hook de traducción
  const uploadsDisabled = true;
  const [filesPending, setFilesPending] = useState([]);
  const [projectFiles, setProjectFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [showFileLimit, setShowFileLimit] = useState(false);

  // Puedes ajustar aquí la cantidad de archivos permitidos
  const FILE_LIMIT = 20;

  // ----------------------------------------------------------------
  // Efectos
  // ----------------------------------------------------------------
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        )
      );
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    const fetchProjectFiles = async () => {
      if (project?.id && user?.uid) {
        try {
          const files = await ProjectFilesService.getProjectFiles(user.uid, project.id);
          setProjectFiles(files);
        } catch (error) {
          console.error('Error fetching project files:', error);
          setProjectFiles([]);
        }
      }
    };
    fetchProjectFiles();
  }, [project?.id, user?.uid]);

  // ----------------------------------------------------------------
  // Manejo de DropZone
  // ----------------------------------------------------------------
  const onDrop = useCallback(
    (acceptedFiles) => {
      if (uploadsDisabled) {
        return;
      }
      const totalFiles = filesPending.length + acceptedFiles.length;

      if (totalFiles > FILE_LIMIT) {
        setShowFileLimit(true);
        // Solo tomamos los archivos que caben dentro del límite
        const remainingSlots = FILE_LIMIT - filesPending.length;
        acceptedFiles = acceptedFiles.slice(0, remainingSlots);
      }

      const newFiles = acceptedFiles.map((file) => ({
        id: Math.random().toString(36).substr(2, 9),
        file,
        type: getFileType(file.name),
      }));
      setFilesPending((prev) => [...prev, ...newFiles]);
    },
    [filesPending]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    noClick: uploadsDisabled,
    noKeyboard: uploadsDisabled,
    disabled: uploadsDisabled,
  });

  // ----------------------------------------------------------------
  // Manejo de archivos pendientes
  // ----------------------------------------------------------------
  const removeFile = (fileId) => {
    setFilesPending((prev) => prev.filter((f) => f.id !== fileId));
  };

  // ----------------------------------------------------------------
  // Manejo de eliminación de archivos existentes
  // ----------------------------------------------------------------
  const handleDeleteClick = (file) => {
    setDeletingFile(file);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeletingFile(null);
    setDeleteConfirmOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingFile) return;

    setIsDeleting(true);
    try {
      await ProjectFilesService.deleteProjectFile(
        user.uid,
        project.id,
        deletingFile.fileName
      );

      // Actualizar lista de archivos
      const updatedFiles = await ProjectFilesService.getProjectFiles(user.uid, project.id);
      setProjectFiles(updatedFiles);

      handleDeleteCancel();
    } catch (error) {
      console.error('[FilesView] Error deleting file:', error);
      alert(t('projectViews.files.deleteError') || 'Error deleting file');
    } finally {
      setIsDeleting(false);
    }
  };

  // ----------------------------------------------------------------
  // Manejo de subida
  // ----------------------------------------------------------------
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSave = async () => {
    if (filesPending.length === 0) {
      if (onClose) onClose();
      return;
    }
    setIsSaving(true);

    try {
      // Extraer los archivos File del estado
      const filesToUpload = filesPending.map(item => item.file);

      // Subir usando el nuevo sistema (todo el procesamiento se hace en el backend)
      const result = await ProjectFilesService.uploadProjectFiles(
        user.uid,
        project.id,
        filesToUpload
      );

      console.log('[FilesView] Upload result:', result);

      // Mostrar mensaje de éxito
      if (result.processed > 0) {
        const message = result.failed > 0
          ? t('projectViews.files.uploadPartialSuccess', {
              processed: result.processed,
              failed: result.failed
            })
          : t('projectViews.files.uploadSuccess');
        alert(message);
      }

      // Mostrar errores si hubo
      if (result.errors && result.errors.length > 0) {
        console.error('[FilesView] Upload errors:', result.errors);
      }

      // Limpiar archivos pendientes
      setFilesPending([]);

      // Actualizar lista de archivos
      const updatedFiles = await ProjectFilesService.getProjectFiles(user.uid, project.id);
      setProjectFiles(updatedFiles);

      if (onClose) onClose();
    } catch (err) {
      console.error('[FilesView] Error uploading files:', err);
      alert(t('projectViews.files.uploadError') + ': ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ----------------------------------------------------------------
  // Contenido principal (no modal)
  // ----------------------------------------------------------------
  // NOTA: en esta sección se ha movido el AlertContainer
  // dentro del contenedor con maxWidth para que no ocupe
  // todo el ancho de la pantalla.
  const content = (
    <Box
      sx={{
        // Este Box es el contenedor más externo.
        // Ponemos "display: flex" y "flexDirection: column"
        // para luego poder hacer sticky con la sección del botón.
        width: '100%',
        px: isModal ? 0 : 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: isModal
            ? '100%'
            : {
                xs: '100%',
                sm: '100%',
                md: '800px',
                lg: '900px',
              },
        }}
      >
        {showFileLimit && (
          <AlertContainer
            message={t('projectViews.files.maxLimitReached')}
            iconColor="#f59e0b"
            onClose={() => setShowFileLimit(false)}
          />
        )}

        {/* Encabezado (cuando no es modal) */}
        {!isModal && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={onBack}
                size="small"
                sx={{
                  mr: 1,
                  color: isWallpaperMode
                    ? 'rgba(255, 255, 255, 0.8)'
                    : (theme.palette.mode === 'dark' ? 'grey.400' : 'grey.700'),
                }}
              >
                <ArrowLeft />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  color: isWallpaperMode ? '#FFFFFF' : 'inherit',
                }}
              >
                {t('projectViews.files.title')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Sección de Dropzone */}
        <Box
          {...getRootProps()}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: '16px',
            cursor: uploadsDisabled ? 'not-allowed' : 'pointer',
            opacity: uploadsDisabled ? 0.6 : 1,
            backdropFilter: uploadsDisabled
              ? (isWallpaperMode ? 'blur(10px)' : 'none')
              : 'blur(10px)',
            WebkitBackdropFilter: uploadsDisabled
              ? (isWallpaperMode ? 'blur(10px)' : 'none')
              : 'blur(10px)',
            backgroundColor:
              !uploadsDisabled && (isDragging || isDragActive)
                ? (isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.18)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)'))
                : (isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.03)'
                    : 'rgba(0, 0, 0, 0.02)')),
            border: '2px dashed',
            borderColor:
              !uploadsDisabled && (isDragging || isDragActive)
                ? '#0385FF'
                : (isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.35)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.1)')),
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor:
                uploadsDisabled
                  ? (isWallpaperMode
                    ? 'rgba(255, 255, 255, 0.12)'
                    : (theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.03)'
                      : 'rgba(0, 0, 0, 0.02)'))
                  : (isWallpaperMode
                    ? 'rgba(255, 255, 255, 0.18)'
                    : (theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.03)')),
            },
          }}
        >
          <input {...getInputProps()} />
          <Upload
            size={48}
            style={{
              marginBottom: theme.spacing(2),
              color:
                isDragging || isDragActive
                  ? '#0385FF'
                  : (isWallpaperMode
                    ? 'rgba(255, 255, 255, 0.75)'
                    : (theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.5)'
                      : 'rgba(0, 0, 0, 0.5)')),
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color:
                isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.75)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.5)'
                    : 'rgba(0, 0, 0, 0.5)'),
            }}
          >
            {uploadsDisabled
              ? 'Coming Soon'
              : (isMobile ? t('projectViews.files.touchToUpload') : t('projectViews.files.dropHere'))}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1,
              color:
                isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.6)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.4)'
                    : 'rgba(0, 0, 0, 0.4)'),
            }}
          >
            {uploadsDisabled ? 'Uploads disabled' : t('projectViews.files.maxFiles')}
          </Typography>
        </Box>

        {/* Listado de archivos pendientes */}
        {filesPending.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                fontSize: '0.9rem',
                fontWeight: 500,
              }}
            >
              {t('projectViews.files.filesToUpload')}
            </Typography>
            <List>
              {filesPending.map((item) => (
                <ListItem
                  key={item.id}
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: '12px',
                    backgroundColor:
                      isWallpaperMode
                        ? 'rgba(255, 255, 255, 0.12)'
                        : (theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.03)'
                          : 'rgba(0, 0, 0, 0.02)'),
                    '&:hover': {
                      backgroundColor:
                        isWallpaperMode
                          ? 'rgba(255, 255, 255, 0.18)'
                          : (theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.08)'
                            : 'rgba(0, 0, 0, 0.05)'),
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <FileIcon fileType={item.type} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.file.name}
                    secondary={formatFileSize(item.file.size)}
                  />
                  <IconButton
                    onClick={() => removeFile(item.id)}
                    size="small"
                    sx={{
                      color: isWallpaperMode
                        ? 'rgba(255, 255, 255, 0.7)'
                        : (theme.palette.mode === 'dark' ? 'grey.400' : 'grey.700'),
                      '&:hover': {
                        color: theme.palette.error.main,
                      },
                    }}
                  >
                    <X size={18} />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Listado de archivos existentes */}
        {projectFiles.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                fontSize: '0.9rem',
                fontWeight: 500,
              }}
            >
              {t('projectViews.files.existingFiles')}
            </Typography>
            <List>
              {projectFiles.map((item, index) => (
                <ListItem
                  key={item.fileName || index}
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: '12px',
                    backgroundColor:
                      isWallpaperMode
                        ? 'rgba(255, 255, 255, 0.12)'
                        : (theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.03)'
                          : 'rgba(0, 0, 0, 0.02)'),
                    '&:hover': {
                      backgroundColor:
                        isWallpaperMode
                          ? 'rgba(255, 255, 255, 0.18)'
                          : (theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.08)'
                            : 'rgba(0, 0, 0, 0.05)'),
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <FileIcon fileType={item.type || 'file'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.fileName}
                    secondary={
                      <>
                        {`${t('projectViews.files.size')}: ${formatFileSize(item.size || 0)}`}
                        {item.compressed && ' • Compressed'}
                        {item.storage === 's3' && ' • S3'}
                      </>
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(item)}
                      sx={{
                        color: theme.palette.error.main,
                        '&:hover': {
                          backgroundColor: theme.palette.error.main + '1A',
                        },
                      }}
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>

      {/* Para que el botón se mantenga siempre visible (no-modal):
          Usamos "position: sticky; bottom: 0;" dentro del mismo contenedor
          donde sucede el scroll, o en uno superior (depende de tu layout).
          Aquí asumimos que el scroll es global y no necesitamos altura fija. 
          Si tu layout hace scroll en otro contenedor, ajusta en consecuencia. */}
      {!isModal && filesPending.length > 0 && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            borderColor: 'divider',
            py: 2,
            mt: 2,
            zIndex: 10,
          }}
        >
          <StyledButton onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <CircularProgress
                size={20}
                color="inherit"
                sx={{ color: isWallpaperMode ? '#000000' : 'inherit' }}
              />
            ) : (
              t('projectViews.files.save')
            )}
          </StyledButton>
        </Box>
      )}
    </Box>
  );

  // ----------------------------------------------------------------
  // Diálogo de confirmación de borrado
  // ----------------------------------------------------------------
  const deleteConfirmDialog = (
    <Dialog
      open={deleteConfirmOpen}
      onClose={handleDeleteCancel}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          bgcolor: isWallpaperMode
            ? 'rgba(128, 128, 128, 0.3)'
            : (theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'),
          backgroundImage: 'none',
          border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
          backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
          WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          pt: 3,
          px: 3,
          fontSize: '1.1rem',
          fontWeight: 500,
          color: isWallpaperMode ? '#FFFFFF' : 'inherit'
        }}
      >
        {t('projectViews.files.deleteFile')}
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        <Typography>
          {t('projectViews.files.deleteConfirmation')}{' '}
          <strong>{deletingFile?.fileName}</strong>?
          {t('projectViews.files.deleteWarning')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, display: 'flex', gap: 1 }}>
        <Button
          onClick={handleDeleteCancel}
          sx={{
              borderRadius: '20px',
              textTransform: 'none',
              color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#fff' : '#000'),
            }}
            disabled={isDeleting}
          >
          {t('projectViews.files.cancel')}
        </Button>
        <Button
          onClick={handleDeleteConfirm}
          sx={{
            borderRadius: '20px',
            textTransform: 'none',
            backgroundColor: theme.palette.error.main,
            color: '#fff',
            '&:hover': {
              backgroundColor: theme.palette.error.dark,
            },
          }}
          disabled={isDeleting}
        >
          {isDeleting ? <CircularProgress size={20} color="inherit" /> : t('projectViews.files.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ----------------------------------------------------------------
  // Render final, diferenciando el modo modal o normal
  // ----------------------------------------------------------------
  if (isModal) {
    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: '20px',
              bgcolor: isWallpaperMode
                ? 'rgba(128, 128, 128, 0.3)'
                : (theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'),
              backgroundImage: 'none',
              border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
              backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
              WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
            },
          }}
        >
          <DialogTitle
            sx={{
              pt: 3,
              pb: 2,
              px: 3,
              fontSize: '1.1rem',
              fontWeight: 500,
              borderBottom: 1,
              borderColor:
                isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.2)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.1)'),
              color: isWallpaperMode ? '#FFFFFF' : 'inherit',
            }}
          >
            {t('projectViews.files.addFiles')}
          </DialogTitle>

          <DialogContent
            // Si quieres que las acciones queden fijas
            // con scroll interno, podrías hacer algo como
            // sx={{ maxHeight: '60vh', overflowY: 'auto' }}
            sx={{ mt: 2, p: 3 }}
          >
            {content}
          </DialogContent>

          <DialogActions
            sx={{
              // Para mantener fijo el footer en el dialog:
              // position: 'sticky',
              // bottom: 0,
              // zIndex: 10,
              // bgcolor: theme.palette.background.paper,
              px: 3,
              pb: 3,
              display: 'flex',
              gap: 1,
            }}
          >
            <Button
              onClick={onClose}
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                px: 3,
                color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#fff' : '#000'),
                '&:hover': {
                  backgroundColor: isWallpaperMode
                    ? 'rgba(255, 255, 255, 0.12)'
                    : (theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.05)'),
                },
              }}
              disabled={isSaving}
            >
              {t('projectViews.files.cancel')}
            </Button>

            <StyledButton
              onClick={handleSave}
              disabled={filesPending.length === 0 || isSaving}
            >
              {isSaving ? (
                <CircularProgress
                  size={20}
                  color="inherit"
                  sx={{ color: isWallpaperMode ? '#000000' : 'inherit' }}
                />
              ) : (
                t('projectViews.files.save')
              )}
            </StyledButton>
          </DialogActions>
        </Dialog>
        {deleteConfirmDialog}
      </>
    );
  }

  // Modo normal (no modal):
  return (
    <>
      <Box>
        {content}
        {/* En modo normal, el botón "Save" ya está sticky dentro de "content"
            solo si hay archivos pendientes. 
            Si quieres mostrar siempre el botón, muévelo de allí. */}
      </Box>
      {deleteConfirmDialog}
    </>
  );
};

export default FilesView;
